/**
 * 流式缓冲区管理器
 *
 * 用于高效处理大文件的流式读取，支持：
 * - 自动缓冲区扩展和紧凑化
 * - 类型化数据读取（little-endian）
 * - 进度回调
 *
 * 基于 PlayCanvas StreamBuf 实现
 */
export class StreamBuf {
  /** 流读取器 */
  readonly reader: ReadableStreamDefaultReader<Uint8Array>

  /** 进度回调函数 */
  readonly progressFunc?: (bytes: number) => void

  /** 缓冲区数据 */
  data: Uint8Array | null = null

  /** DataView 用于类型化读取 */
  view: DataView | null = null

  /** 读取头指针（已读取数据的起始位置） */
  head = 0

  /** 写入尾指针（未读取数据的结束位置） */
  tail = 0

  /**
   * 创建流式缓冲区
   * @param reader 流读取器
   * @param progressFunc 可选的进度回调函数
   */
  constructor(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    progressFunc?: (bytes: number) => void
  ) {
    this.reader = reader
    this.progressFunc = progressFunc
  }

  /**
   * 从流中读取下一块数据
   * @throws 如果流在预期之前结束
   */
  async read(): Promise<void> {
    const { value, done } = await this.reader.read()

    if (done) {
      throw new Error('Stream finished before end of data')
    }

    this.push(value)
    this.progressFunc?.(value.byteLength)
  }

  /**
   * 追加数据到缓冲区
   *
   * 策略：
   * 1. 如果缓冲区足够大：直接复制或移动数据
   * 2. 如果缓冲区不够：创建更大的缓冲区并复制数据
   *
   * @param data 要追加的数据
   */
  push(data: Uint8Array): void {
    if (!this.data) {
      // 首次初始化
      this.data = data
      this.view = new DataView(this.data.buffer)
      this.tail = data.length
    } else {
      const remaining = this.tail - this.head
      const newSize = remaining + data.length

      if (this.data.length >= newSize) {
        // 缓冲区足够大，可以容纳合并后的数据
        if (this.head > 0) {
          // 需要移动已有数据到起始位置，然后追加新数据
          this.data.copyWithin(0, this.head, this.tail)
          this.data.set(data, remaining)
          this.head = 0
          this.tail = newSize
        } else {
          // 不需要移动，直接追加新数据
          this.data.set(data, this.tail)
          this.tail += data.length
        }
      } else {
        // 缓冲区太小，需要扩容
        const tmp = new Uint8Array(newSize)
        if (this.head > 0 || this.tail < this.data.length) {
          // 移动已有数据到新缓冲区的起始位置
          tmp.set(this.data.subarray(this.head, this.tail), 0)
        } else {
          tmp.set(this.data, 0)
        }
        tmp.set(data, remaining)
        this.data = tmp
        this.view = new DataView(this.data.buffer)
        this.head = 0
        this.tail = newSize
      }
    }
  }

  /**
   * 紧凑化缓冲区
   *
   * 移除已读取的数据（从缓冲区头部移除），为后续数据腾出空间
   */
  compact(): void {
    if (this.head > 0 && this.data) {
      this.data.copyWithin(0, this.head, this.tail)
      this.tail -= this.head
      this.head = 0
    }
  }

  /**
   * 获取剩余未读字节数
   */
  get remaining(): number {
    return this.tail - this.head
  }

  // ============================================================
  // 类型化读取方法（little-endian）
  // ============================================================

  /**
   * 读取 int8。
   *
   * @returns 当前读头位置的有符号 8 位整数
   */
  getInt8(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getInt8(this.head)
    this.head++
    return result
  }

  /**
   * 读取 uint8。
   *
   * @returns 当前读头位置的无符号 8 位整数
   */
  getUint8(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getUint8(this.head)
    this.head++
    return result
  }

  /**
   * 读取 int16（little-endian）。
   *
   * @returns 当前读头位置的有符号 16 位整数
   */
  getInt16(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getInt16(this.head, true)
    this.head += 2
    return result
  }

  /**
   * 读取 uint16（little-endian）。
   *
   * @returns 当前读头位置的无符号 16 位整数
   */
  getUint16(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getUint16(this.head, true)
    this.head += 2
    return result
  }

  /**
   * 读取 int32（little-endian）。
   *
   * @returns 当前读头位置的有符号 32 位整数
   */
  getInt32(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getInt32(this.head, true)
    this.head += 4
    return result
  }

  /**
   * 读取 uint32（little-endian）。
   *
   * @returns 当前读头位置的无符号 32 位整数
   */
  getUint32(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getUint32(this.head, true)
    this.head += 4
    return result
  }

  /**
   * 读取 float32（little-endian）。
   *
   * @returns 当前读头位置的单精度浮点数
   */
  getFloat32(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getFloat32(this.head, true)
    this.head += 4
    return result
  }

  /**
   * 读取 float64（little-endian）。
   *
   * @returns 当前读头位置的双精度浮点数
   */
  getFloat64(): number {
    if (!this.view) throw new Error('StreamBuf not initialized')
    const result = this.view.getFloat64(this.head, true)
    this.head += 8
    return result
  }

  /**
   * 获取底层缓冲区数据（只读访问）
   */
  get buffer(): Uint8Array | null {
    return this.data
  }
}
